// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Crew Goals Manager
 * 
 * Tracks crew member goals, progress, and completion rewards
 */
import { GOAL_STATUS, generateRandomGoals, getAvailableGoals } from '../../config/CrewGoalsConfig.js';
import EventBus, { EVENT_TYPES } from '../system/EventBus.js';
import STORAGE_KEYS from '../system/STORAGE_KEYS';

class CrewGoalsManager {
  constructor() {
    this.crewGoals = {}; // crewId -> [goals]
    this.loadFromStorage();
  }

  /**
   * Initialize goals for a crew member
   */
  initializeGoals(crewMember) {
    if (!this.crewGoals[crewMember.id]) {
      this.crewGoals[crewMember.id] = generateRandomGoals(crewMember, 2);
      this.saveToStorage();
    }
  }

  /**
   * Get goals for a crew member
   */
  getCrewGoals(crewId) {
    return this.crewGoals[crewId] || [];
  }

  /**
   * Update goal progress
   */
  updateProgress(crewId, goalType, amount = 1) {
    const goals = this.getCrewGoals(crewId);
    const completedGoals = [];

    goals.forEach(goal => {
      if (goal.status !== GOAL_STATUS.ACTIVE) return;

      let shouldUpdate = false;
      
      // Check if this progress update applies to this goal
      // eslint-disable-next-line default-case
      switch (goal.goalId) {
        case 'explore_systems':
          shouldUpdate = goalType === 'system_visit';
          break;
        case 'survive_combat':
          shouldUpdate = goalType === 'combat_survive';
          break;
        case 'earn_credits':
          shouldUpdate = goalType === 'credits_earned';
          break;
        case 'level_up':
          shouldUpdate = goalType === 'level_up';
          break;
        case 'pilot_efficiency':
          shouldUpdate = goalType === 'efficient_jump';
          break;
        case 'engineer_repairs':
          shouldUpdate = goalType === 'repair_complete';
          break;
        case 'gunner_accuracy':
          shouldUpdate = goalType === 'combat_kill';
          break;
        case 'medic_healing':
          shouldUpdate = goalType === 'crew_healed';
          break;
        case 'scientist_scans':
          shouldUpdate = goalType === 'scan_complete';
          break;
        case 'make_friend':
          shouldUpdate = goalType === 'friendship_formed';
          break;
        case 'mentor_rookie':
          shouldUpdate = goalType === 'rookie_trained';
          break;
        case 'team_player':
          shouldUpdate = goalType === 'group_activity';
          break;
      }

      if (shouldUpdate) {
        goal.progress = Math.min(goal.progress + amount, goal.goalData.target);
        
        // Check if goal is completed
        if (goal.progress >= goal.goalData.target) {
          goal.status = GOAL_STATUS.COMPLETED;
          goal.completedAt = Date.now();
          completedGoals.push({ crewId, goal });
        }
      }
    });

    if (completedGoals.length > 0) {
      this.saveToStorage();
      this.processCompletedGoals(completedGoals);
    }

    return completedGoals;
  }

  /**
   * Process completed goals and award rewards
   */
  processCompletedGoals(completedGoals) {
    completedGoals.forEach(({ crewId, goal }) => {
      // Award XP and morale bonus
      EventBus.emit(EVENT_TYPES.CREW_GOAL_COMPLETED, {
        crewId,
        goal: goal.goalData,
        xpReward: goal.goalData.xpReward,
        moraleBonus: goal.goalData.moraleBonus
      });
    });
  }

  /**
   * Assign new goal to crew member
   */
  assignGoal(crewId, goalId) {
    const crewMember = this.getCrewMember(crewId);
    if (!crewMember) return false;

    const availableGoals = getAvailableGoals(crewMember, this.getCrewGoals(crewId));
    const goalTemplate = availableGoals.find(g => g.id === goalId);
    
    if (!goalTemplate) return false;

    if (!this.crewGoals[crewId]) {
      this.crewGoals[crewId] = [];
    }

    this.crewGoals[crewId].push({
      goalId: goalTemplate.id,
      goalData: goalTemplate,
      status: GOAL_STATUS.ACTIVE,
      progress: 0,
      assignedAt: Date.now()
    });

    this.saveToStorage();
    return true;
  }

  /**
   * Remove goal from crew member
   */
  removeGoal(crewId, goalId) {
    if (!this.crewGoals[crewId]) return false;

    const index = this.crewGoals[crewId].findIndex(g => g.goalId === goalId);
    if (index === -1) return false;

    this.crewGoals[crewId].splice(index, 1);
    this.saveToStorage();
    return true;
  }

  /**
   * Get crew member (helper method)
   */
  getCrewMember(crewId) {
    // Import here to avoid circular dependency
    const CrewManager = require('./CrewManager.js').default;
    return CrewManager.getCrewMember(crewId);
  }

  /**
   * Get goal statistics
   */
  getGoalStats(crewId) {
    const goals = this.getCrewGoals(crewId);
    return {
      total: goals.length,
      active: goals.filter(g => g.status === GOAL_STATUS.ACTIVE).length,
      completed: goals.filter(g => g.status === GOAL_STATUS.COMPLETED).length,
      failed: goals.filter(g => g.status === GOAL_STATUS.FAILED).length
    };
  }

  /**
   * Clean up goals for removed crew members
   */
  cleanupGoals(existingCrewIds) {
    const toRemove = Object.keys(this.crewGoals).filter(id => !existingCrewIds.includes(id));
    toRemove.forEach(id => delete this.crewGoals[id]);
    if (toRemove.length > 0) {
      this.saveToStorage();
    }
  }

  /**
   * Reset all goals
   */
  reset() {
    this.crewGoals = {};
    this.saveToStorage();
  }

  /**
   * Save to localStorage
   */
  saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEYS.CREW_GOALS, JSON.stringify(this.crewGoals));
    } catch (error) {
      console.error('[CrewGoalsManager] Failed to save to localStorage:', error);
    }
  }

  /**
   * Load from localStorage
   */
  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CREW_GOALS);
      if (data) {
        this.crewGoals = JSON.parse(data);
      }
    } catch (error) {
      console.error('[CrewGoalsManager] Failed to load from localStorage:', error);
      this.crewGoals = {};
    }
  }
}

// Singleton instance
const crewGoalsManager = new CrewGoalsManager();
export default crewGoalsManager;